import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useMediaQuery } from "@mui/material";
import { ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import { use100vh } from "react-div-100vh";
import jsPDF from "jspdf";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../components/ui/context-menu";
import { cn } from "../lib/utils";


const ThumbnailManager = React.memo(({ selectedImages }) => {
  const loadedPaths = useRef(new Set());
  useEffect(() => {
    const preloadImages = async () => {
      const newImages = selectedImages.filter(
        (img) => img.thumbnailUrl && !loadedPaths.current.has(img.path_lower)
      );
      if (newImages.length === 0) return;
      await Promise.all(
        newImages.map(
          (image) =>
            new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                loadedPaths.current.add(image.path_lower);
                resolve();
              };
              img.onerror = resolve;
              img.src = `data:image/jpeg;base64,${image.thumbnailUrl}`;
            })
        )
      );
    };
    preloadImages();
  }, [selectedImages]);
  return null;
});

const ZoomableCanvas = ({
  canvasRef,
  isZoomed,
  setIsZoomed,
  zoomOrigin,
  setZoomOrigin,
  onRightClickImage,
  imagePositions,
  setHighlightedImageId,
  setContextMenuImage,
}) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragged, setDragged] = useState(false);
  const dragSpeedFactor = 0.5;
  const dragThreshold = 5; // pixels to move before considering it a drag

  useEffect(() => {
    if (!isZoomed) {
      setDragOffset({ x: 0, y: 0 });
    }
    // Reset dragged state when zoom changes to fix click detection
    setDragged(false);
    setIsDragging(false);
  }, [isZoomed]);

  const handleStart = (clientX, clientY) => {
    if (isZoomed) {
      setIsDragging(true);
      setDragged(false);
      setDragStart({
        x: clientX - dragOffset.x / dragSpeedFactor,
        y: clientY - dragOffset.y / dragSpeedFactor,
      });
    }
  };

  const handleMove = (clientX, clientY) => {
    if (isZoomed && isDragging) {
      const deltaX = clientX - dragStart.x;
      const deltaY = clientY - dragStart.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Only consider it dragged if moved beyond threshold
      if (distance > dragThreshold) {
        const newX = deltaX * dragSpeedFactor;
        const newY = deltaY * dragSpeedFactor;
        setDragOffset({ x: newX, y: newY });
        setDragged(true);
      }
    }
  };

  const handleEnd = () => {
    setIsDragging(false);
  };


  const handleMouseDown = (e) => handleStart(e.clientX, e.clientY);
  const handleMouseMove = (e) => {
    handleMove(e.clientX, e.clientY);
  };
  const handleMouseUp = () => handleEnd();
  const handleMouseLeave = () => {
    handleMouseUp();
  };
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      e.preventDefault();
      handleStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  };
  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      e.preventDefault();
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  };
  const handleTouchEnd = handleEnd;

  const getClickedImage = (e) => {
    if (!imagePositions || imagePositions.length === 0 || isZoomed) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const canvas = canvasRef.current;

    // Account for canvas objectFit: contain scaling
    const canvasAspect = canvas.width / canvas.height;
    const containerAspect = rect.width / rect.height;

    let scaleX, scaleY, offsetX = 0, offsetY = 0;

    if (canvasAspect > containerAspect) {
      // Canvas is wider than container - fit to width
      scaleX = scaleY = rect.width / canvas.width;
      offsetY = (rect.height - canvas.height * scaleY) / 2;
    } else {
      // Canvas is taller than container - fit to height
      scaleX = scaleY = rect.height / canvas.height;
      offsetX = (rect.width - canvas.width * scaleX) / 2;
    }

    // Convert screen coordinates to canvas coordinates
    const x = ((e.clientX - rect.left - offsetX) / scaleX);
    const y = ((e.clientY - rect.top - offsetY) / scaleY);

    // Find which image was clicked
    for (const imagePos of imagePositions) {
      if (x >= imagePos.x && x <= imagePos.x + imagePos.width &&
          y >= imagePos.y && y <= imagePos.y + imagePos.height) {
        return imagePos.image;
      }
    }
    return null;
  };

  const handleClick = (e) => {
    if (!dragged) {
      const clickedImage = getClickedImage(e);
      if (clickedImage) {
        console.log('Left-click on image:', clickedImage.name);
        setHighlightedImageId(clickedImage.name);
        setContextMenuImage(clickedImage);

        // Create synthetic right-click event to trigger context menu
        const syntheticEvent = new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          clientX: e.clientX,
          clientY: e.clientY,
          button: 2 // right mouse button
        });

        // Dispatch to the same target
        e.target.dispatchEvent(syntheticEvent);

        onRightClickImage && onRightClickImage(clickedImage, e);
      }
    }
  };

  const handleDoubleClick = (e) => {
    if (!dragged) {
      if (!isZoomed) {
        // Zoom in: Calculate zoom origin from click position
        const rect = containerRef.current.getBoundingClientRect();
        setZoomOrigin({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
        setIsZoomed(true);
      } else {
        // Zoom out
        setIsZoomed(false);
      }
    }
  };

  const handleContextMenu = (e) => {
    console.log('Right-click detected');

    const clickedImage = getClickedImage(e);
    if (clickedImage) {
      console.log('Right-click on image:', clickedImage.name);
      setHighlightedImageId(clickedImage.name);
      setContextMenuImage(clickedImage);
      onRightClickImage && onRightClickImage(clickedImage, e);
      // DON'T preventDefault here - let the context menu show
      return;
    }

    // Only prevent default context menu if NOT clicking on an image
    console.log('No image clicked, preventing default context menu');
    e.preventDefault();
  };

  const canvasStyle = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    cursor: isZoomed ? "grab" : "default",
    transform: isZoomed
      ? `scale(2.3) translate(${dragOffset.x}px, ${dragOffset.y}px)`
      : "none",
    transformOrigin: isZoomed
      ? `${zoomOrigin.x * 100}% ${zoomOrigin.y * 100}%`
      : "center",
    transition: isDragging ? "none" : "transform 0.3s ease-out",
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "calc(100% - 80px)",
          overflow: "hidden",
          position: "relative",
        }}
        onContextMenu={handleContextMenu}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <canvas ref={canvasRef} style={canvasStyle} />
      </div>

    </>
  );
};

const SelectedImagesGrid = ({ selectedImages, jobName, embedded = false, onRemoveImage, lastChangedImage }) => {
  const canvasRef = useRef(null);
  const isSmallScreen = useMediaQuery("(max-width:1200px)");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageBreaks, setPageBreaks] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 0.5, y: 0.5 });
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const previousSelectedImagesLength = useRef(selectedImages.length);

  // Context menu state
  const [contextMenuImage, setContextMenuImage] = useState(null);
  const [imagePositions, setImagePositions] = useState([]);
  const [highlightedImageId, setHighlightedImageId] = useState(null);
  const previousSelectedImages = useRef([]);

  const pageData = useMemo(() => {
    const cols = 6;
    const rows = 6;
    const maxSlotsPerPage = cols * rows;
    let currentSlots = 0;
    let breaks = [];
    for (let i = 0; i < selectedImages.length; i++) {
      const slotsNeeded = 1;
      if (currentSlots + slotsNeeded > maxSlotsPerPage) {
        breaks.push(i);
        currentSlots = slotsNeeded;
      } else {
        currentSlots += slotsNeeded;
      }
    }
    if (
      breaks.length === 0 ||
      breaks[breaks.length - 1] !== selectedImages.length
    ) {
      breaks.push(selectedImages.length);
    }
    return breaks;
  }, [selectedImages]);

  useEffect(() => {
    setPageBreaks(pageData);
  }, [pageData]);

  const totalPages = Math.max(1, pageBreaks.length);

  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  // Auto-navigate to the page containing the changed image
  useEffect(() => {
    const currentLength = selectedImages.length;
    const previousLength = previousSelectedImagesLength.current;

    // Only navigate if we have a specific changed image and the length changed
    if (lastChangedImage && currentLength !== previousLength) {
      const cols = 6;
      const rows = 6;
      const maxSlotsPerPage = cols * rows;

      // Find the index of the changed image in the current selectedImages array
      const changedImageIndex = selectedImages.findIndex(
        (img) => img.name === lastChangedImage.name
      );

      // If image was added (found in current array), navigate to its page
      if (changedImageIndex !== -1) {
        const pageForChangedImage = Math.floor(changedImageIndex / maxSlotsPerPage);
        setCurrentPage(pageForChangedImage);
      } else {
        // If image was removed (not found in current array), find where it was in the previous array
        const previousImageIndex = previousSelectedImages.current.findIndex(
          (img) => img.name === lastChangedImage.name
        );

        if (previousImageIndex !== -1) {
          // Navigate to the page where the image was before removal
          const pageForRemovedImage = Math.floor(previousImageIndex / maxSlotsPerPage);
          // Ensure the page is still valid after removal
          const maxValidPage = Math.max(0, Math.ceil(currentLength / maxSlotsPerPage) - 1);
          setCurrentPage(Math.min(pageForRemovedImage, maxValidPage));
        }
      }
    }

    // Update refs for next comparison
    previousSelectedImagesLength.current = currentLength;
    previousSelectedImages.current = selectedImages;
  }, [selectedImages.length, lastChangedImage, selectedImages]);

  useEffect(() => {
    (async () => {
      await renderCanvasForPage(currentPage);
    })();
  }, [currentPage, pageBreaks, highlightedImageId]);

  const renderCanvasForPage = async (page, options = {}) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Dual resolution: half size for viewing, full size for downloads
    const { downloadMode = false } = options;
    const scaleFactor = downloadMode ? 1 : 0.5;
    const canvasWidth = Math.round(4200 * scaleFactor);
    const canvasHeight = Math.round(6000 * scaleFactor);

    // Set canvas dimensions only if they haven't been set or are different
    // This prevents the white flash caused by clearing canvas on every render
    if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
    }

    // Create offscreen canvas for double-buffering to prevent flashing
    const offscreenCanvas = document.createElement("canvas");
    offscreenCanvas.width = canvasWidth;
    offscreenCanvas.height = canvasHeight;
    const offscreenCtx = offscreenCanvas.getContext("2d", { willReadFrequently: true });
    offscreenCtx.imageSmoothingEnabled = true;
    offscreenCtx.imageSmoothingQuality = "high";

    offscreenCtx.fillStyle = "#ffffff";
    offscreenCtx.fillRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);

    const cols = 6;
    const rows = 6;
    const padding = Math.round(90 * scaleFactor);
    const outerPadding = Math.round(100 * scaleFactor);
    const availableWidth =
      canvasWidth - 2 * outerPadding - padding * (cols - 1);
    const cellWidth = availableWidth / cols;
    const cellHeight = cellWidth * 1.5;

    const startIdx = page === 0 ? 0 : pageBreaks[page - 1];
    const endIdx = pageBreaks[page];
    const pageImages = selectedImages.slice(startIdx, endIdx);

    const occupiedCells = new Set();
    let maxRow = -1;
    const positions = [];

    const findNextPosition = (isWide) => {
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          if (!occupiedCells.has(`${row},${col}`)) {
            if (!isWide) return { row, col };
            if (col < cols - 1 && !occupiedCells.has(`${row},${col + 1}`)) {
              return { row, col };
            }
          }
        }
      }
      return null;
    };

    for (const image of pageImages) {
      if (image.thumbnailUrl) {
        const img = await new Promise((resolve) => {
          const imgEl = new Image();
          imgEl.onload = () => resolve(imgEl);
          imgEl.onerror = resolve;
          imgEl.src = `data:image/jpeg;base64,${image.thumbnailUrl}`;
        });
        if (!img) continue;

        const imgAspect = img.width / img.height;
        const isWide = imgAspect > 1;
        const position = findNextPosition(isWide);
        if (!position) continue;

        const { row, col } = position;
        maxRow = Math.max(maxRow, row);

        if (isWide && col < cols - 1) {
          occupiedCells.add(`${row},${col}`);
          occupiedCells.add(`${row},${col + 1}`);

          const x = outerPadding + col * (cellWidth + padding);
          const y = outerPadding + row * (cellHeight + padding);
          const doubleCellWidth = cellWidth * 2 + padding;

          let drawWidth = doubleCellWidth;
          let drawHeight = drawWidth / imgAspect;

          if (drawHeight > cellHeight) {
            drawHeight = cellHeight;
            drawWidth = drawHeight * imgAspect;
          }

          const drawX = x + (doubleCellWidth - drawWidth) / 2;
          const drawY = y + (cellHeight - drawHeight) / 2;

          offscreenCtx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

          // Draw blue border if this image is highlighted (solid line as requested)
          if (highlightedImageId && image.name === highlightedImageId) {
            offscreenCtx.strokeStyle = "rgba(59,130,246,0.8)";
            offscreenCtx.lineWidth = Math.round(8 * scaleFactor);
            const borderPadding = Math.round(5 * scaleFactor);
            offscreenCtx.strokeRect(drawX - borderPadding, drawY - borderPadding, drawWidth + borderPadding * 2, drawHeight + borderPadding * 2);
          }

          // Track position for context menu
          positions.push({
            image,
            x: drawX,
            y: drawY,
            width: drawWidth,
            height: drawHeight
          });
        } else {
          occupiedCells.add(`${row},${col}`);

          const x = outerPadding + col * (cellWidth + padding);
          const y = outerPadding + row * (cellHeight + padding);
          const cellAspect = cellWidth / cellHeight;

          let drawWidth = cellWidth;
          let drawHeight = cellHeight;
          let drawX = x;
          let drawY = y;

          if (imgAspect > cellAspect) {
            drawHeight = drawWidth / imgAspect;
            drawY += (cellHeight - drawHeight) / 2;
          } else {
            drawWidth = drawHeight * imgAspect;
            drawX += (cellWidth - drawWidth) / 2;
          }

          offscreenCtx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

          // Draw blue border if this image is highlighted (solid line as requested)
          if (highlightedImageId && image.name === highlightedImageId) {
            offscreenCtx.strokeStyle = "rgba(59,130,246,0.8)";
            offscreenCtx.lineWidth = Math.round(8 * scaleFactor);
            const borderPadding = Math.round(5 * scaleFactor);
            offscreenCtx.strokeRect(drawX - borderPadding, drawY - borderPadding, drawWidth + borderPadding * 2, drawHeight + borderPadding * 2);
          }

          // Track position for context menu
          positions.push({
            image,
            x: drawX,
            y: drawY,
            width: drawWidth,
            height: drawHeight
          });
        }
      } else {
        const position = findNextPosition(false);
        if (!position) continue;

        const { row, col } = position;
        maxRow = Math.max(maxRow, row);

        const x = outerPadding + col * (cellWidth + padding);
        const y = outerPadding + row * (cellHeight + padding);

        occupiedCells.add(`${row},${col}`);
        offscreenCtx.fillStyle = "#333";
        offscreenCtx.fillRect(x, y, cellWidth, cellHeight);
      }
    }

    // Update image positions state for context menu
    setImagePositions(positions);

    // Copy the completed offscreen canvas to the visible canvas (prevents flashing)
    ctx.drawImage(offscreenCanvas, 0, 0);

    return { maxRow, outerPadding, padding, cellHeight };
  };

  const handleRightClickImage = (image) => {
    console.log('Setting context menu image:', image.name);
    setContextMenuImage(image);
  };

  const handleRemoveImage = () => {
    if (contextMenuImage && onRemoveImage) {
      console.log('REMOVING IMAGE via callback:', contextMenuImage.name);
      onRemoveImage(contextMenuImage);
    } else if (contextMenuImage && !onRemoveImage) {
      console.warn('No onRemoveImage callback provided');
    }
    setContextMenuImage(null);
  };

  const downloadJPEGs = async () => {
    if (!canvasRef.current) return;
    setIsDownloading(true);
    const originalPage = currentPage;
    const link = document.createElement("a");

    try {
      for (let page = 0; page < totalPages; page++) {
        const result = await renderCanvasForPage(page, { downloadMode: true });
        await new Promise((resolve) => requestAnimationFrame(resolve));

        let cropHeight = canvasRef.current.height;
        if (
          page === totalPages - 1 &&
          result &&
          typeof result.maxRow === "number" &&
          result.maxRow >= 0
        ) {
          const computedHeight =
            result.outerPadding +
            result.maxRow * (result.cellHeight + result.padding) +
            result.cellHeight +
            50;
          cropHeight = Math.min(computedHeight, canvasRef.current.height);
        }

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = canvasRef.current.width;
        tempCanvas.height = cropHeight;
        const tempCtx = tempCanvas.getContext("2d");
        tempCtx.drawImage(
          canvasRef.current,
          0,
          0,
          canvasRef.current.width,
          cropHeight,
          0,
          0,
          canvasRef.current.width,
          cropHeight
        );

        const blob = await new Promise((resolve) => {
          tempCanvas.toBlob(resolve, "image/jpeg", 0.95);
        });

        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `${jobName}-Storyboard-${page + 1}.jpg`;
        link.click();
        URL.revokeObjectURL(url);
        await new Promise((r) => setTimeout(r, 200));
      }
    } catch (error) {
      console.error("Error downloading JPEGs:", error);
    } finally {
      setIsDownloading(false);
      await renderCanvasForPage(originalPage);
    }
  };

  const downloadPDF = async () => {
    if (!canvasRef.current) return;
    setIsDownloading(true);
    const originalPage = currentPage;
    let pdf;

    try {
      for (let page = 0; page < totalPages; page++) {
        await renderCanvasForPage(page, { downloadMode: true });
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const cropHeight = canvasRef.current.height;

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = canvasRef.current.width;
        tempCanvas.height = cropHeight;
        const tempCtx = tempCanvas.getContext("2d");
        tempCtx.drawImage(
          canvasRef.current,
          0,
          0,
          canvasRef.current.width,
          cropHeight,
          0,
          0,
          canvasRef.current.width,
          cropHeight
        );

        const imgData = tempCanvas.toDataURL("image/jpeg", 0.95);
        const pageWidth = tempCanvas.width;
        const pageHeight = tempCanvas.height;

        if (page === 0) {
          pdf = new jsPDF({
            unit: "px",
            format: [pageWidth, pageHeight],
          });
          pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, pageHeight);
        } else {
          pdf.addPage([pageWidth, pageHeight]);
          pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, pageHeight);
        }
      }

      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `${jobName}-Storyboard.pdf`;
      link.click();
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setIsDownloading(false);
      await renderCanvasForPage(originalPage);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
        padding: "10px",
        backgroundColor: "#f0f0f0",
        height: `calc(${use100vh()}px - 70px)`,
        position: "relative",
        marginTop: embedded ? "0px" : "64px",
      }}
    >
      <ThumbnailManager selectedImages={selectedImages} />
      <ContextMenu onOpenChange={(open) => {
        console.log('Context menu open state:', open);
        if (!open) {
          // Reset context menu image and highlight when menu closes
          setContextMenuImage(null);
          setHighlightedImageId(null);
        }
      }}>
        <ContextMenuTrigger asChild>
          <div style={{ width: '100%', height: 'calc(100% - 80px)' }}>
            <ZoomableCanvas
              canvasRef={canvasRef}
              isZoomed={isZoomed}
              setIsZoomed={setIsZoomed}
              zoomOrigin={zoomOrigin}
              setZoomOrigin={setZoomOrigin}
              onRightClickImage={handleRightClickImage}
              imagePositions={imagePositions}
              setHighlightedImageId={setHighlightedImageId}
              setContextMenuImage={setContextMenuImage}
            />
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          {contextMenuImage ? (
            <>
              <ContextMenuItem onClick={handleRemoveImage} className="text-red-600 focus:text-red-600 focus:bg-red-50">
                Remove Image
              </ContextMenuItem>
              <ContextMenuItem disabled className="text-gray-800 dark:text-gray-200">
                {contextMenuImage.name || 'Unnamed Image'}
              </ContextMenuItem>
            </>
          ) : (
            <ContextMenuItem disabled className="text-gray-400">
              No image selected
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "6px",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          position: "absolute",
          bottom: "24px",
          left: "50%",
          transform: "translateX(-50%)",
          minHeight: "48px",
          maxHeight: "48px",
          width: "auto",
          maxWidth: "calc(100% - 48px)",
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.1)"
        }}
      >
        {/* Navigation Controls */}
        <Button
          variant="default"
          size="icon"
          onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={currentPage === 0}
          className="h-9 w-9 bg-gray-600 hover:bg-gray-700 text-white disabled:bg-gray-300"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {/* Page Counter */}
        <span className="font-medium text-sm text-gray-700 whitespace-nowrap min-w-[60px] text-center px-2 tabular-nums">
          {currentPage + 1} / {totalPages}
        </span>

        <Button
          variant="default"
          size="icon"
          onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={currentPage === totalPages - 1}
          className="h-9 w-9 bg-gray-600 hover:bg-gray-700 text-white disabled:bg-gray-300"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        {/* Separator */}
        <Separator orientation="vertical" className="h-6" />

        {/* Zoom Controls */}
        <Button
          variant="default"
          size="icon"
          onClick={() => setIsZoomed(!isZoomed)}
          className={cn(
            "h-9 w-9 text-white",
            isZoomed
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-600 hover:bg-gray-700"
          )}
        >
          {isZoomed ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />}
        </Button>

        {/* Separator */}
        <Separator orientation="vertical" className="h-6" />

        {/* Download Menu */}
        <DropdownMenu open={downloadMenuOpen} onOpenChange={setDownloadMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="default"
              size={isSmallScreen ? "icon" : "default"}
              disabled={selectedImages.length === 0 || isDownloading}
              className={cn(
                "bg-green-600 hover:bg-green-700",
                isSmallScreen ? "h-9 w-9" : "h-9 px-4"
              )}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  {!isSmallScreen && <span className="ml-2">Download</span>}
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" side="top">
            <DropdownMenuItem
              onClick={async () => {
                setDownloadMenuOpen(false);
                await downloadJPEGs();
              }}
              disabled={isDownloading}
            >
              <Download className="mr-2 h-4 w-4" />
              <span>Download JPEGs</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                setDownloadMenuOpen(false);
                await downloadPDF();
              }}
              disabled={isDownloading}
            >
              <Download className="mr-2 h-4 w-4" />
              <span>Download PDF</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

    </div>
  );
};

export default React.memo(SelectedImagesGrid);
